import { toast } from 'react-hot-toast';
import { X } from 'lucide-react';
import api, { 
  githubApi, 
  slackApi, 
  jiraApi, 
  linearApi, 
  notionApi 
} from '../services/api.ts';

interface IntegrationCardProps {
  id: string;
  name: string;
  description: string;
  icon: any;
  bgColor: string;
  hoverColor: string;
  authUrl: string;
  isConnected: boolean;
  onStatusChange?: () => void;  // Add this to refresh status after disconnect
  compact?: boolean;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  id,
  name,
  description,
  icon: Icon,
  bgColor,
  hoverColor,
  authUrl,
  isConnected,
  onStatusChange,
  compact = false
}) => {
  const handleConnect = async (url: string) => {
    try {
      console.log(`Connecting to ${name}...`);
      // Use the appropriate API based on the integration ID
      const integrationApis: { [key: string]: any } = {
        github: githubApi,
        slack: slackApi,
        jira: jiraApi,
        notion: notionApi,
        linear: linearApi,
      };

      const integrationApi = integrationApis[id];
      if (integrationApi) {
        console.log(`Using ${id} API...`);
        await integrationApi.startAuth();
      } else {
        console.warn(`No specific API for ${id}, using fallback`);
        // Fallback to generic API call if no specific integration API exists
        const cleanUrl = url.replace(/^\/api\//, '/');
        const response = await api.get(cleanUrl);
        if (response.data?.auth_url) {
          window.location.href = response.data.auth_url;
        }
      }
    } catch (error) {
      console.error('Error connecting:', error);
      toast.error(`Failed to connect to ${name}`);
    }
  };

  const handleDisconnect = async () => {
    try {
      const integrationApis: { [key: string]: any } = {
        github: githubApi,
        slack: slackApi,
        jira: jiraApi,
        notion: notionApi,
        linear: linearApi,
      };

      const integrationApi = integrationApis[id];
      if (integrationApi?.disconnect) {
        // Use specific integration disconnect if available
        await integrationApi.disconnect();
      } else {
        // Fallback to generic disconnect
        const response = await api.post(`/${id}/disconnect`);
      }
      
      toast.success(`Successfully disconnected from ${name}`);
      if (onStatusChange) onStatusChange();
    } catch (error) {
      console.error(`Error disconnecting from ${name}:`, error);
      toast.error(`Failed to disconnect from ${name}`);
    }
  };

  return (
    <div className={`bg-white rounded-lg ${
      compact ? 'p-3 border border-gray-200' : 'p-6 shadow-sm'
    }`}>
      <div className="flex items-center">
        <div className={`${compact ? 'w-8 h-8' : 'w-12 h-12'} flex-shrink-0 rounded-lg ${bgColor} flex items-center justify-center`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div className="ml-4 flex-1">
          <h3 className={`font-medium ${compact ? 'text-sm' : 'text-base'} text-gray-900`}>{name}</h3>
          {!compact && (
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          )}
        </div>
        {isConnected ? (
          <button
            onClick={handleDisconnect}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors"
          >
            Disconnect
          </button>
        ) : (
          <button
            onClick={() => handleConnect(authUrl)}
            className={`px-3 py-1.5 rounded-md text-xs font-medium text-white ${bgColor} ${hoverColor}`}
          >
            Connect
          </button>
        )}
      </div>
    </div>
  );
};

export default IntegrationCard; 